/**
 * Builds storage keys scoped to a BCM tenant so UI state does not leak across harbour switches.
 * sessionStorage is per browser tab; localStorage is shared across tabs of the same origin.
 */
export function buildTenantScopedStorageKey(tenantId: number | null | undefined, suffix: string): string {
    const scope = tenantId != null && tenantId > 0 ? String(tenantId) : 'unknown';
    return `u2b.tenant.${scope}.${suffix}`;
}

export function buildTenantScopedSessionKey(tenantId: number | null | undefined, suffix: string): string {
    return buildTenantScopedStorageKey(tenantId, suffix);
}

export function buildTenantScopedLocalStorageKey(tenantId: number | null | undefined, suffix: string): string {
    return buildTenantScopedStorageKey(tenantId, suffix);
}

const TABLE_ROW_HIGHLIGHT_IDS_SUFFIX = '_row_highlight_ids';

export function buildTableRowHighlightSessionKey(
    tenantId: number | null | undefined,
    tableId: string,
): string {
    return buildTenantScopedSessionKey(tenantId, `${tableId}${TABLE_ROW_HIGHLIGHT_IDS_SUFFIX}`);
}

export function setTableRowHighlightIds(
    tenantId: number | null | undefined,
    tableId: string,
    rowIds: Array<number | string>,
): void {
    if (!rowIds.length) {
        return;
    }

    sessionStorage.setItem(
        buildTableRowHighlightSessionKey(tenantId, tableId),
        JSON.stringify(rowIds.map(id => String(id))),
    );
}

export function readTableRowHighlightIds(
    tenantId: number | null | undefined,
    tableId: string,
): string[] {
    const raw = sessionStorage.getItem(buildTableRowHighlightSessionKey(tenantId, tableId));

    if (!raw) {
        return [];
    }

    try {
        const parsed = JSON.parse(raw) as unknown;

        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed.map(id => String(id));
    } catch {
        return [];
    }
}

export function clearTableRowHighlightIds(tenantId: number | null | undefined, tableId: string): void {
    sessionStorage.removeItem(buildTableRowHighlightSessionKey(tenantId, tableId));
}
